import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { CollapseController } from '../common/controllers/CollapseController.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'

import style from './AccordionPanel.css'

/**
 * Accordion Panel holds the content revealed by its
 * [Accordion Trigger](/components/accordion-trigger/). It must be placed inside
 * an [Accordion Item](/components/accordion-item/) and animates its height as it
 * opens and closes.
 *
 * @status draft
 * @category container
 * @lightDom true
 *
 * @slot - Default slot for the panel content.
 *
 * @attr {boolean} data-open - Present while the panel is open (or opening).
 * @attr {boolean} data-closed - Present while the panel is closed.
 * @attr {boolean} data-starting-style - Present for the first frame of the open transition.
 * @attr {boolean} data-ending-style - Present while the close transition runs.
 *
 * @cssprop [--n-accordion-panel-height] - Set during the open/close animation to the measured content height. Read it to coordinate custom animations.
 */
@customElement('nord-accordion-panel')
export default class AccordionPanel extends LitElement {
  /**
   * Whether the panel is open. Set by the parent
   * [Accordion Item](/components/accordion-item/).
   */
  @property({ reflect: true, type: Boolean }) open = false

  // The height animation + transition-status data-attributes live in the shared
  // controller (also used by the collapsible panel).
  private collapse = new CollapseController(this, {
    open: () => this.open,
    heightVar: '--n-accordion-panel-height',
  })

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    this.setAttribute('role', 'region')
    // Reflect the initial state without animating.
    this.hidden = !this.open
  }

  // Render in the light DOM so the content is the element's own children and the
  // injected stylesheet applies (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-accordion-panel': AccordionPanel
  }
}
